import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, LogOut, ChevronRight, ChevronDown, Search } from 'lucide-react';
import { NavData, SecondaryNavData, IntelligenceNavData } from '../Constant';
import SessionsListSection from './SessionsListSection';
import BrandLockup from '../BrandLockup';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { useAgents } from '../../hooks';
import { cn } from '../../lib/utils';
import { DEFAULT_COPILOT_ID, DEFAULT_COPILOT_NAME } from '../../constants/defaultCopilot';

const AGENT_MENU_WIDTH = 260;
const AGENT_MENU_MAX_HEIGHT = 320;

function AgentFilterMenu({ agents, selectedAgent, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(a => (a?.agent_name || '').toLowerCase().includes(q));
  }, [agents, query]);

  const closeMenu = () => {
    setOpen(false);
    setQuery('');
  };

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const gap = 8;
      // Open into the main content area to the right of the sidebar trigger
      let left = rect.right + gap;
      let top = rect.top;

      if (left + AGENT_MENU_WIDTH > window.innerWidth - 8) {
        left = Math.max(8, rect.left - AGENT_MENU_WIDTH - gap);
      }
      if (top + AGENT_MENU_MAX_HEIGHT > window.innerHeight - 8) {
        top = Math.max(8, window.innerHeight - AGENT_MENU_MAX_HEIGHT - 8);
      }
      if (top < 8) top = 8;

      setCoords({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      closeMenu();
    };
    const onKey = e => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  return (
    <div className="relative min-w-0 max-w-[55%]">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 max-w-full text-xs text-mute hover:text-ink bg-transparent border-0 cursor-pointer p-0"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={selectedAgent?.label || 'All'}>
        <span className="truncate">{selectedAgent?.label || 'All'}</span>
        <ChevronDown className={cn('w-3 h-3 flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: coords.top, left: coords.left, width: AGENT_MENU_WIDTH, maxHeight: AGENT_MENU_MAX_HEIGHT }}
            className="fixed z-[120] flex flex-col rounded-xl border border-shell-border bg-paper-2 shadow-soft overflow-hidden"
            role="listbox"
            onClick={e => e.stopPropagation()}>
            <div className="p-2 border-b border-shell-border flex-shrink-0">
              <div className="flex items-center gap-2 rounded-lg bg-paper border border-shell-border px-2.5 py-1.5">
                <Search className="w-3.5 h-3.5 text-mute flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search agents..."
                  className="w-full min-w-0 bg-transparent text-xs text-ink placeholder:text-mute outline-none"
                />
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-3 text-xs text-mute">No agents found</p>
              ) : (
                filtered.map(data => {
                  const isActive =
                    (selectedAgent?.value ?? null) === (data?.id ?? null) ||
                    (selectedAgent?.label === data?.agent_name && !selectedAgent?.value && !data?.id);
                  return (
                    <button
                      key={data?.id ?? data?.agent_name ?? 'all'}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      title={data?.agent_name}
                      onClick={() => {
                        onSelect({ label: data?.agent_name, value: data?.id });
                        closeMenu();
                      }}
                      className={cn(
                        'w-full text-left px-3 py-2 text-xs truncate transition-colors',
                        isActive
                          ? 'bg-space-soft text-primary font-medium'
                          : 'text-mute hover:text-ink hover:bg-white/5',
                      )}>
                      {data?.agent_name}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

const SideBar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [agentData, setAgentData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState({
    label: 'All',
    value: '',
  });

  const navigate = useNavigate();
  const location = useLocation();

  const { user, onLogout } = useContextHook(AuthContext, v => ({
    user: v.user,
    onLogout: v.onLogout,
  }));

  const getAllAgentData = useAgents();

  useEffect(() => {
    if (getAllAgentData.isSuccess) {
      setAgentData([
        { agent_name: 'All', id: '' },
        { agent_name: DEFAULT_COPILOT_NAME, id: DEFAULT_COPILOT_ID },
        ...(getAllAgentData?.data?.agents || []),
      ]);
    }
  }, [getAllAgentData.isSuccess, getAllAgentData?.data?.agents]);

  useEffect(() => {
    if (!collapsed) {
      document.body.classList.add('sidebar-active');
    } else {
      document.body.classList.remove('sidebar-active');
    }
  }, [collapsed]);

  const isActive = item => {
    const path = `/${item.path}`;
    if (item.exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const initial = user?.first_name?.trim()?.[0]?.toUpperCase() || 'U';
  const displayName = user?.first_name || 'User';

  const linkClass = (active, justifyCenter) =>
    cn(
      'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors relative',
      justifyCenter && 'justify-center',
      active
        ? 'bg-white/10 text-ink font-medium'
        : 'text-mute hover:text-ink hover:bg-white/5',
    );

  return (
    <aside
      className={cn(
        'flex-shrink-0 bg-paper-2 border-r border-shell-border flex flex-col h-screen sticky top-0 transition-all duration-200 z-50',
        collapsed ? 'w-16' : 'w-64',
      )}>
      <div
        className={cn(
          'h-16 flex items-center border-b border-shell-border flex-shrink-0',
          collapsed ? 'px-3 justify-center gap-1' : 'px-4 justify-between',
        )}>
        <BrandLockup collapsed={collapsed} markSize={11} />
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="p-1.5 rounded-md text-mute hover:text-ink hover:bg-white/10"
          title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-3 sidebar-scroll">
        <ul className="space-y-0.5 px-2">
          {NavData.map(item => {
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={`/${item.path}`}
                  title={collapsed ? item.name : undefined}
                  className={linkClass(active, collapsed)}>
                  {active ? (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-space rounded-r" />
                  ) : null}
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>

        <div className="mt-2 px-2">
          {!collapsed && (
            <div className="flex items-center mb-2 px-2">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-mute">
                · Intelligence
              </span>
            </div>
          )}
          <ul className="space-y-0.5">
            {IntelligenceNavData.map(item => {
              const active = isActive(item);
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={`/${item.path}`}
                    title={collapsed ? item.name : undefined}
                    className={linkClass(active, collapsed)}>
                    {active ? (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-space rounded-r" />
                    ) : null}
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {!collapsed && <span className="truncate">{item.name}</span>}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>

        {!collapsed && (
          <div className="mt-6 px-2">
            <div className="flex items-center justify-between gap-2 mb-2 px-2">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-mute flex-shrink-0">
                · Chats
              </span>
              <AgentFilterMenu
                agents={agentData}
                selectedAgent={selectedAgent}
                onSelect={setSelectedAgent}
              />
            </div>
            <div className="px-1">
              <SessionsListSection selectedAgent={selectedAgent} />
            </div>
          </div>
        )}
      </nav>

      <div className="flex-shrink-0 border-t border-shell-border px-2 py-2">
        <ul className="space-y-0.5">
          {SecondaryNavData.map(item => {
            if (item.adminOnly && user?.plan?.type !== 'admin') return null;
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={`/${item.path}`}
                  title={collapsed ? item.name : undefined}
                  className={linkClass(active, collapsed)}>
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && item.name}
                </NavLink>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={onLogout}
              title={collapsed ? 'Logout' : undefined}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-mute hover:text-ink hover:bg-white/5',
                collapsed && 'justify-center',
              )}>
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!collapsed && 'Logout'}
            </button>
          </li>
        </ul>
      </div>

      <div
        className="border-t border-shell-border p-3 flex-shrink-0"
        onClick={() => navigate('/settings')}
        onKeyDown={e => e.key === 'Enter' && navigate('/settings')}
        role="button"
        tabIndex={0}>
        <div
          className={cn(
            'flex items-center gap-3 px-2 py-2 rounded-md hover:bg-white/5 cursor-pointer',
            collapsed && 'justify-center',
          )}>
          <div className="w-8 h-8 rounded-full bg-ink flex items-center justify-center text-paper text-sm font-extrabold flex-shrink-0">
            {initial}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">{displayName}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-mute" />
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default SideBar;
